import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Canvas, useFrame } from "@react-three/fiber";
import { useGLTF, useAnimations } from "@react-three/drei";
import { clone as skeletonClone } from "three/examples/jsm/utils/SkeletonUtils.js";
import * as THREE from "three";
import { PLAYER_COLORS, avatarFileForPlayer } from "./three/constants";
import { useGameStore } from "../store";
import { CheckCircle2, Clock, Trophy } from "lucide-react";

// 6 avatars choisis pour le mode attract (silhouettes lisibles à distance, mix de tailles)
const KIOSK_AVATARS = [
  "animal-fox",
  "animal-bunny",
  "animal-lion",
  "animal-penguin",
  "animal-panda",
  "animal-tiger",
];

// Lanes en X — 6 persos, espacement ~3.2u, span 16u (tient dans le cadre horizontal du FOV)
const LANE_OFFSETS_X = [-8, -5, -1.6, 1.6, 5, 8];

// Formation en V (presque) : centre proche caméra, ailes en arrière.
// Ordre : fox, bunny, lion, penguin, panda, tiger
//   ailes (fox, tiger) → reculées (Z négatif)
//   épaules (bunny, panda) → milieu
//   pointe (lion, penguin) → avancés vers la caméra
const LANE_OFFSETS_Z = [-1.6, 0, 1.6, 1.6, 0, -1.6];

// --- Personnage qui court face caméra (mode attract) ---
function RunningCharacter({
  avatarFile,
  laneX,
  baseZ,
  phaseOffset,
}: {
  avatarFile: string;
  laneX: number;
  baseZ: number;
  phaseOffset: number;
}) {
  const groupRef = useRef<THREE.Group>(null!);
  const modelRef = useRef<THREE.Group>(null!);
  const { scene, animations } = useGLTF(avatarFile);

  const clonedScene = useMemo(() => {
    const c = skeletonClone(scene);
    c.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        child.frustumCulled = false;
      }
    });
    return c;
  }, [scene]);

  const { actions, names } = useAnimations(animations, modelRef);

  useEffect(() => {
    const runName =
      names.find((n) => /run/i.test(n)) ||
      names.find((n) => /walk/i.test(n)) ||
      names[0];
    if (!runName || !actions[runName]) return;
    const action = actions[runName];
    action.reset().setLoop(THREE.LoopRepeat, Infinity).fadeIn(0.2).play();
    // Décale la phase de chaque animation pour que les jambes ne soient pas synchro
    // → on dirait des coureurs vraiment indépendants, pas des clones
    action.time = phaseOffset * 0.25;
    return () => {
      action.fadeOut(0.2);
    };
  }, [actions, names, phaseOffset]);

  useFrame(({ clock }) => {
    if (!groupRef.current) return;
    const t = clock.getElapsedTime();
    // Bobbing vertical (rebond du pas)
    groupRef.current.position.y =
      Math.abs(Math.sin(t * 6 + phaseOffset)) * 0.18;
    // Oscillation en Z autour de la position V (réduite à 0.5 pour que la formation V reste lisible) →
    // ils se dépassent/se rattrapent légèrement, comme une course en groupe rapproché.
    const speedVariation = 0.55 + phaseOffset * 0.18;
    groupRef.current.position.z =
      baseZ + Math.sin(t * speedVariation + phaseOffset * 2.3) * 0.5;
  });

  return (
    <group ref={groupRef} position={[laneX, 0, baseZ]}>
      {/* Rotation Y = 0 — les GLB de cette collection ont leur forward déjà aligné caméra */}
      <group ref={modelRef} scale={1.6} rotation={[0, 0, 0]}>
        <primitive object={clonedScene} />
      </group>
    </group>
  );
}

// Sol fait de tuiles Scene.glb (herbe + arbres + touffes) qui défile vers -Z
// (s'éloigne de la caméra → impression que les persos courent VERS nous).
// La scène glb est rotée Y=π/2 pour aligner son axe X (longueur ~30.88) sur l'axe Z monde.
const KIOSK_SCENE_URL = "/bg/Scene.glb";
const KIOSK_SCENE_SCALE = 4;
const KIOSK_TILE_LENGTH = 30.88 * KIOSK_SCENE_SCALE; // ~123.52 unités monde par tuile
const KIOSK_SCROLL_SPEED = 24;

function ScrollingSceneTiles() {
  const groupRef = useRef<THREE.Group>(null!);
  const { scene } = useGLTF(KIOSK_SCENE_URL);

  // 3 tuiles : une AVANT la caméra (+T, prête à entrer en foreground), une en foreground (0),
  // une en arrière-plan (-T). Quand le scroll arrive à -T, on wrap +T pour revenir à l'état initial
  // — les 3 tuiles étant des clones identiques, le wrap est invisible.
  const tiles = useMemo(() => {
    return Array.from({ length: 3 }, () => scene.clone(true));
  }, [scene]);

  // Offsets relatifs au groupe : +T, 0, -T
  const TILE_OFFSETS = useMemo(
    () => [KIOSK_TILE_LENGTH, 0, -KIOSK_TILE_LENGTH],
    [],
  );

  // Activer les ombres sur les meshes des clones
  useEffect(() => {
    tiles.forEach((tile) => {
      tile.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
    });
  }, [tiles]);

  useFrame((_, delta) => {
    if (!groupRef.current) return;
    groupRef.current.position.z -= delta * KIOSK_SCROLL_SPEED;
    if (groupRef.current.position.z < -KIOSK_TILE_LENGTH) {
      groupRef.current.position.z += KIOSK_TILE_LENGTH;
    }
  });

  return (
    <group ref={groupRef}>
      {tiles.map((tile, i) => (
        <primitive
          key={i}
          object={tile}
          position={[0, 0, TILE_OFFSETS[i]]}
          rotation={[0, Math.PI / 2, 0]}
          scale={KIOSK_SCENE_SCALE}
        />
      ))}
    </group>
  );
}

useGLTF.preload(KIOSK_SCENE_URL);

function CameraTarget() {
  useFrame(({ camera }) => {
    camera.lookAt(0, 1.1, -3);
  });
  return null;
}

// --- Speed lines 3D : barres élongées qui filent de l'horizon vers la caméra.
// Étant dans la scène, elles suivent automatiquement la perspective (= vraies lignes
// radiantes depuis le vanishing point, plus alignées que le CSS conic gradient). ---
const SPEED_LINE_COUNT = 80;

function SpeedLines3D() {
  const meshRef = useRef<THREE.InstancedMesh>(null!);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  // État par instance : position 3D + vitesse propre.
  // Les lignes partent près de la caméra et filent vers le fond (-Z) → cohérent avec
  // la perception "le monde s'éloigne des coureurs qui foncent vers nous".
  const lines = useMemo(
    () =>
      Array.from({ length: SPEED_LINE_COUNT }, () => ({
        x: (Math.random() - 0.5) * 22,
        y: Math.random() * 8 + 0.2,
        z: 9 - Math.random() * 140, // répartis du foreground (+9) jusqu'au fond (-131)
        speed: 35 + Math.random() * 45,
      })),
    [],
  );

  useFrame((_, delta) => {
    if (!meshRef.current) return;
    for (let i = 0; i < SPEED_LINE_COUNT; i++) {
      const l = lines[i];
      l.z -= l.speed * delta;
      // Recycle quand la ligne disparaît dans le fond → réinjectée près de la caméra
      if (l.z < -130) {
        l.z = 10 + Math.random() * 5;
        l.x = (Math.random() - 0.5) * 22;
        l.y = Math.random() * 8 + 0.2;
        l.speed = 35 + Math.random() * 45;
      }
      dummy.position.set(l.x, l.y, l.z);
      dummy.updateMatrix();
      meshRef.current.setMatrixAt(i, dummy.matrix);
    }
    meshRef.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh
      ref={meshRef}
      args={[undefined, undefined, SPEED_LINE_COUNT]}
      frustumCulled={false}
    >
      {/* Strip très fin en X/Y, élongé en Z (axe de la course) → la perspective le voit comme un trait radial */}
      <boxGeometry args={[0.06, 0.06, 5]} />
      <meshBasicMaterial
        color="#ffffff"
        transparent
        opacity={0.85}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </instancedMesh>
  );
}

// Scène 3D plein écran : vue frontale, persos qui courent vers la caméra,
// décor Scene.glb tilé qui défile loin de nous (impression de course).
function RunningScene() {
  return (
    <Canvas
      shadows
      camera={{ fov: 60, near: 0.1, far: 300, position: [0, 3.4, 12] }}
      style={{ width: "100%", height: "100%" }}
      gl={{ alpha: true, antialias: true }}
    >
      {/* Fog couleur sakura-100 (matche le bg de la page) : commence à 25u, totalement
          opaque à 130u → cache le bout des tuiles + le wrap qui se passe derrière. */}
      <fog attach="fog" args={["#FFE4EF", 25, 130]} />
      <ambientLight intensity={0.7} />
      <directionalLight
        position={[8, 22, 8]}
        intensity={1.2}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-near={1}
        shadow-camera-far={200}
        shadow-camera-left={-60}
        shadow-camera-right={60}
        shadow-camera-top={50}
        shadow-camera-bottom={-50}
        shadow-bias={-0.001}
      />
      <hemisphereLight args={["#fff5fb", "#88b878", 0.5]} />
      <ScrollingSceneTiles />
      <SpeedLines3D />
      {KIOSK_AVATARS.map((name, i) => (
        <RunningCharacter
          key={name}
          avatarFile={`/assets/avatars3D/${name}.glb`}
          laneX={LANE_OFFSETS_X[i]}
          baseZ={LANE_OFFSETS_Z[i]}
          phaseOffset={i * 1.1}
        />
      ))}
      <CameraTarget />
    </Canvas>
  );
}

KIOSK_AVATARS.forEach((name) =>
  useGLTF.preload(`/assets/avatars3D/${name}.glb`),
);

// --- Mini avatar 3D pour les cartes joueurs en mode lobby ---
function MiniAvatar({ avatarFile }: { avatarFile: string }) {
  const modelRef = useRef<THREE.Group>(null!);
  const { scene, animations } = useGLTF(avatarFile);

  const clonedScene = useMemo(() => {
    const c = skeletonClone(scene);
    c.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        child.frustumCulled = false;
      }
    });
    return c;
  }, [scene]);

  const { actions, names } = useAnimations(animations, modelRef);

  useEffect(() => {
    // Idle si dispo, sinon walk/run pour avoir au moins un peu de vie
    const animName =
      names.find((n) => /idle/i.test(n)) ||
      names.find((n) => /walk/i.test(n)) ||
      names[0];
    if (!animName || !actions[animName]) return;
    const action = actions[animName];
    action.reset().setLoop(THREE.LoopRepeat, Infinity).fadeIn(0.2).play();
    return () => {
      action.fadeOut(0.2);
    };
  }, [actions, names]);

  // Légère rotation pour donner de la vie (oscillation autour de la position face caméra)
  useFrame(({ clock }) => {
    if (!modelRef.current) return;
    modelRef.current.rotation.y = Math.sin(clock.getElapsedTime() * 0.6) * 0.4;
  });

  return (
    <group ref={modelRef} scale={1.3} position={[0, -0.7, 0]}>
      <primitive object={clonedScene} />
    </group>
  );
}

function MiniAvatarScene({ avatarFile }: { avatarFile: string }) {
  return (
    <Canvas
      camera={{ fov: 35, position: [0, 0.6, 4.5] }}
      gl={{ alpha: true, antialias: true }}
      style={{ width: "100%", height: "100%" }}
    >
      <ambientLight intensity={0.9} />
      <directionalLight position={[2, 5, 3]} intensity={0.8} />
      <MiniAvatar avatarFile={avatarFile} />
    </Canvas>
  );
}

// --- Bloc QR commun aux 2 layouts ---
function QRBlock({ joinUrl, size }: { joinUrl: string; size: number }) {
  return (
    <div className="relative">
      <motion.div
        className="absolute -inset-3 rounded-3xl bg-gradient-to-r from-sakura-400 via-lavender-400 to-sky-400 opacity-70 blur-md"
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="relative p-4 bg-white rounded-3xl shadow-kawaii-lg border-4 border-white">
        {joinUrl ? (
          <QRCodeSVG
            value={joinUrl}
            size={size}
            level="H"
            includeMargin={false}
            fgColor="#E91E6C"
            bgColor="#ffffff"
          />
        ) : (
          <div
            style={{ width: size, height: size }}
            className="flex items-center justify-center text-gray-400 font-bold"
          >
            …
          </div>
        )}
      </div>
    </div>
  );
}

// --- Header partagé ---
function KioskHeader({
  title,
  label,
  compact,
  highscoreName,
  highscoreValue,
}: {
  title: string;
  label: string;
  compact?: boolean;
  highscoreName?: string | null;
  highscoreValue?: number | null;
}) {
  const hasScore = highscoreName && highscoreValue != null;
  return (
    <div className="text-center pointer-events-none">
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className={`inline-block ${compact ? "px-6 py-1.5 mb-2" : "px-8 py-2 mb-4"} rounded-full bg-gradient-to-r from-sakura-400 to-lavender-400 shadow-kawaii-lg`}
      >
        <span
          className={`${compact ? "text-base md:text-lg" : "text-xl md:text-2xl"} font-black text-white tracking-[0.4em] uppercase drop-shadow`}
        >
          {label}
        </span>
      </motion.div>
      {title && (
        <motion.h1
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, type: "spring", bounce: 0.5 }}
          className={`${compact ? "text-2xl md:text-4xl" : "text-3xl md:text-5xl"} font-black text-gray-700 drop-shadow-lg`}
        >
          {title}
        </motion.h1>
      )}
      {/* Highscore badge : best-of à battre, édité par l'admin du set. Affiché sous
          le titre, pill amber, taille atténuée pour ne pas concurrencer le titre. */}
      {hasScore && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, type: "spring", bounce: 0.4 }}
          className={`inline-flex items-center gap-2 ${compact ? "mt-2 px-4 py-1 text-sm md:text-base" : "mt-3 px-5 py-1.5 text-base md:text-lg"} rounded-full bg-white/85 backdrop-blur-sm border-2 border-amber-300/70 shadow-kawaii`}
        >
          <Trophy className={`${compact ? "w-4 h-4" : "w-5 h-5"} text-amber-500`} />
          <span className="font-black text-amber-700 tracking-wide">
            {highscoreName} : {highscoreValue!.toLocaleString()}
          </span>
        </motion.div>
      )}
    </div>
  );
}

// --- Layout IDLE : 0 joueur — scène 3D plein écran, overlay header + message + QR ---
function IdleLayout({
  title,
  label,
  joinUrl,
  highscoreName,
  highscoreValue,
}: {
  title: string;
  label: string;
  joinUrl: string;
  highscoreName?: string | null;
  highscoreValue?: number | null;
}) {
  return (
    <motion.div
      key="idle"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="absolute inset-0"
    >
      {/* Scène 3D full screen en fond — speed lines en 3D directement dans la scène */}
      <div className="absolute inset-0">
        <RunningScene />
      </div>

      {/* Logo Think&Sprint collé en haut à gauche, avec un float/wiggle continu */}
      <motion.img
        src="/assets/logo.svg"
        alt="Think&Sprint"
        initial={{ opacity: 0, x: -20 }}
        animate={{
          opacity: 1,
          x: 0,
          y: [0, -6, 0],
          rotate: [-3, 3, -3],
        }}
        transition={{
          opacity: { duration: 0.8 },
          x: { duration: 0.8, type: "spring", bounce: 0.4 },
          y: { duration: 3, repeat: Infinity, ease: "easeInOut" },
          rotate: { duration: 4, repeat: Infinity, ease: "easeInOut" },
        }}
        className="absolute top-4 left-4 w-64 md:w-80 drop-shadow-lg z-20 pointer-events-none"
      />

      {/* Overlay UI : header en haut, footer (Compete + Scan to play + QR) en bas */}
      <div className="relative h-full flex flex-col justify-between p-12 z-10">
        <KioskHeader title={title} label={label} highscoreName={highscoreName} highscoreValue={highscoreValue} />

        {/* Footer 3 colonnes : Compete (gauche), Scan to play (centre), QR (droite) — tous alignés en bas */}
        <div className="w-full flex items-end justify-between gap-8">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6, type: "spring", bounce: 0.4 }}
            className="text-left pointer-events-none"
          >
            <motion.div
              animate={{ rotate: [-2, 2, -2] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="inline-block"
            >
              <p className="text-6xl md:text-8xl font-black leading-[0.95] text-white drop-shadow-lg">
                Compete
              </p>
              <p className="text-4xl md:text-6xl font-black text-gray-600 mt-2 leading-none">
                with your friends
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.7, y: 20 }}
            animate={{
              opacity: 1,
              scale: [1, 1.06, 1],
              y: 0,
            }}
            transition={{
              opacity: { duration: 0.6 },
              y: { duration: 0.6 },
              scale: { duration: 1.8, repeat: Infinity, ease: "easeInOut" },
            }}
            className="inline-flex items-center gap-4 px-8 py-4 rounded-full bg-gradient-to-r from-sakura-500 to-lavender-500 shadow-kawaii-lg border-4 border-white pointer-events-none self-end mb-6"
          >
            <span className="text-3xl md:text-5xl font-black text-white tracking-wider drop-shadow-lg">
              Scan to play
            </span>
            <span className="text-4xl md:text-6xl text-white">→</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.5, rotate: -8 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{
              delay: 0.5,
              type: "spring",
              bounce: 0.55,
              duration: 0.8,
            }}
          >
            <QRBlock joinUrl={joinUrl} size={300} />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

// --- Carte joueur (mode LOBBY) : mini avatar 3D + nom + ready ---
function PlayerCard({
  playerId,
  name,
  color,
  ready,
  index,
}: {
  playerId: string;
  name: string;
  color: string;
  ready: boolean;
  index: number;
}) {
  const avatarFile = useMemo(() => avatarFileForPlayer(playerId), [playerId]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.8 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.8 }}
      transition={{
        type: "spring",
        bounce: 0.55,
        duration: 0.6,
        delay: index * 0.08,
      }}
      className="relative flex flex-col items-center"
    >
      <motion.div
        animate={ready ? { scale: [1, 1.06, 1] } : { y: [0, -4, 0] }}
        transition={{
          duration: ready ? 1.2 : 2.5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="relative w-40 h-40 md:w-48 md:h-48 rounded-blob shadow-kawaii-lg border-4 border-white overflow-hidden"
        style={{ backgroundColor: color }}
      >
        <MiniAvatarScene avatarFile={avatarFile} />
        {ready && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-kawaii z-10"
          >
            <CheckCircle2 className="w-8 h-8 text-emerald-500" />
          </motion.div>
        )}
      </motion.div>
      <p className="mt-3 text-2xl md:text-3xl font-black text-gray-700 tracking-wide max-w-[10ch] truncate">
        {name}
      </p>
      <span
        className={`mt-1 px-3 py-0.5 rounded-full text-xs font-black tracking-widest uppercase ${
          ready
            ? "bg-emerald-100 text-emerald-600"
            : "bg-gray-100 text-gray-400"
        }`}
      >
        {ready ? "Ready" : "Waiting"}
      </span>
    </motion.div>
  );
}

// --- Layout LOBBY ---
function LobbyLayout({
  title,
  label,
  joinUrl,
  countdown,
  players,
  highscoreName,
  highscoreValue,
}: {
  title: string;
  label: string;
  joinUrl: string;
  countdown: number | null;
  players: { id: string; name: string; ready?: boolean }[];
  highscoreName?: string | null;
  highscoreValue?: number | null;
}) {
  return (
    <motion.div
      key="lobby"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="relative h-full flex flex-col items-center justify-between p-10 z-10"
    >
      <KioskHeader title={title} label={label} compact highscoreName={highscoreName} highscoreValue={highscoreValue} />

      <div className="flex-1 flex items-center justify-center w-full">
        <div className="flex gap-8 md:gap-12 flex-wrap justify-center max-w-7xl">
          <AnimatePresence>
            {players.map((p, i) => (
              <PlayerCard
                key={p.id}
                playerId={p.id}
                name={p.name}
                color={PLAYER_COLORS[i % PLAYER_COLORS.length]}
                ready={!!p.ready}
                index={i}
              />
            ))}
          </AnimatePresence>
        </div>
      </div>

      <div className="w-full flex items-end justify-between gap-12 px-4 md:px-12">
        <div className="flex items-center gap-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", bounce: 0.5 }}
          >
            <QRBlock joinUrl={joinUrl} size={240} />
          </motion.div>
          <div className="text-left max-w-[18rem]">
            <p className="text-3xl md:text-4xl font-black text-gray-700 leading-tight">
              Join the race!
            </p>
            <p className="text-sm md:text-base font-bold text-gray-400 mt-1">
              Scan to add yourself to the lobby
            </p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          className="text-right"
        >
          {countdown !== null && countdown > 0 ? (
            <div className="flex flex-col items-end">
              <p className="text-sm md:text-base font-black tracking-[0.3em] uppercase text-gray-400">
                Game starts in
              </p>
              <motion.div
                key={countdown}
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", bounce: 0.6 }}
                className="text-7xl md:text-9xl font-black text-transparent bg-clip-text bg-gradient-to-br from-sakura-500 to-lavender-500 leading-none drop-shadow-lg"
              >
                {countdown}
              </motion.div>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-gray-500">
              <Clock className="w-7 h-7 text-lavender-400 animate-pulse" />
              <div>
                <p className="text-xl md:text-2xl font-black">Get ready!</p>
                <p className="text-sm md:text-base font-bold text-gray-400">
                  Tap <span className="text-emerald-500">READY</span> on your
                  phone
                </p>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}

interface VitrineKioskProps {
  joinUrl: string;
  countdown: number | null;
  // Set ponctuel : id direct partagé depuis /admin/sets.
  questionSetId?: string;
  // Schedule d'un user : ?vitrine=<userId> dans l'URL.
  scheduleUserId?: string;
}

export function VitrineKiosk({
  joinUrl,
  countdown,
  questionSetId,
  scheduleUserId,
}: VitrineKioskProps) {
  const [title, setTitle] = useState<string>("");
  // Highscore best-of du set ponctuel (édité dans /admin/sets). N'existe pas pour
  // les schedules (les sessions sont inline dans le JSON de la schedule, pas des
  // question_sets séparés), donc null en mode schedule.
  const [highscoreName, setHighscoreName] = useState<string | null>(null);
  const [highscoreValue, setHighscoreValue] = useState<number | null>(null);
  const players = useGameStore((s) => s.players);
  const roomHostId = useGameStore((s) => s.roomHostId);

  const visiblePlayers = players.filter((p) => p.id !== roomHostId);
  // Label : "Quiz of the Week" pour une schedule (cadence régulière) ; "Live Quiz" pour un set ponctuel.
  const headerLabel = scheduleUserId ? "Quiz of the Week" : "Live Quiz";

  useEffect(() => {
    let cancelled = false;
    // Deux sources possibles pour le titre courant :
    //   - schedule user : on lit la session active via /api/public/schedules/:userId
    //   - set ponctuel : on lit /api/public/questions/:id
    const url = scheduleUserId
      ? `/api/public/schedules/${encodeURIComponent(scheduleUserId)}`
      : questionSetId
        ? `/api/public/questions/${encodeURIComponent(questionSetId)}`
        : null;
    if (!url) return;
    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const t = scheduleUserId ? data?.currentSession?.title : data?.title;
        setTitle(t || "");
        // Highscore : seulement pour les sets ponctuels (les schedules n'en ont pas).
        if (!scheduleUserId) {
          setHighscoreName(data?.highscoreName || null);
          setHighscoreValue(typeof data?.highscoreValue === "number" ? data.highscoreValue : null);
        } else {
          setHighscoreName(null);
          setHighscoreValue(null);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [questionSetId, scheduleUserId]);

  const isLobby = visiblePlayers.length > 0;

  return (
    <div className="fixed inset-0 overflow-hidden bg-gradient-to-br from-sakura-100 via-white to-lavender-100 font-kawaii">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, rgba(255,107,157,0.12), transparent 60%)",
        }}
        animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.9, 0.5] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
      />

      <AnimatePresence mode="wait">
        {isLobby ? (
          <LobbyLayout
            title={title}
            label={headerLabel}
            joinUrl={joinUrl}
            countdown={countdown}
            players={visiblePlayers}
            highscoreName={highscoreName}
            highscoreValue={highscoreValue}
          />
        ) : (
          <IdleLayout
            title={title}
            label={headerLabel}
            joinUrl={joinUrl}
            highscoreName={highscoreName}
            highscoreValue={highscoreValue}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
